import React from 'react';
import { CurrencyEuroIcon } from '@heroicons/react/24/outline';

interface BudgetItem {
  amount: number;
  description: string;
}

interface BudgetBreakdownProps {
  budgetBreakdown: {
    development: BudgetItem;
    infrastructure: BudgetItem;
    training: BudgetItem;
    maintenance: BudgetItem;
  };
}

const BudgetBreakdown: React.FC<BudgetBreakdownProps> = ({ budgetBreakdown }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const totalBudget = Object.values(budgetBreakdown).reduce((sum, item) => sum + item.amount, 0);

  const getPercentage = (amount: number) => {
    return ((amount / totalBudget) * 100).toFixed(1);
  };

  const getColorClass = (category: string) => {
    const colors = {
      development: 'bg-blue-500',
      infrastructure: 'bg-green-500',
      training: 'bg-yellow-500',
      maintenance: 'bg-purple-500'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-500';
  };

  const getCategoryName = (category: string) => {
    const names = {
      development: 'Sviluppo',
      infrastructure: 'Infrastruttura',
      training: 'Formazione',
      maintenance: 'Manutenzione'
    };
    return names[category as keyof typeof names] || category;
  };

  const budgetItems = Object.entries(budgetBreakdown);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-6">
        <CurrencyEuroIcon className="h-6 w-6 text-green-600 mr-2" />
        <h2 className="text-xl font-bold text-gray-900">Budget Breakdown</h2>
      </div>
      
      {/* Total budget */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(totalBudget)}
          </div>
          <div className="text-sm text-gray-500">Budget Totale</div>
        </div>
      </div>

      {/* Budget breakdown */}
      <div className="space-y-4">
        {budgetItems.map(([category, item]) => (
          <div key={category} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full ${getColorClass(category)} mr-3`}></div>
                <span className="font-medium text-gray-900">
                  {getCategoryName(category)}
                </span>
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900">
                  {formatCurrency(item.amount)}
                </div>
                <div className="text-sm text-gray-500">
                  {getPercentage(item.amount)}%
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 ml-7">
              {item.description}
            </p>
          </div>
        ))}
      </div>

      {/* Visual breakdown */}
      <div className="mt-6">
        <div className="text-sm font-medium text-gray-700 mb-2">Distribuzione Budget</div>
        <div className="flex rounded-lg overflow-hidden h-3">
          {budgetItems.map(([category, item]) => (
            <div
              key={category}
              className={getColorClass(category)}
              style={{ width: `${getPercentage(item.amount)}%` }}
              title={`${getCategoryName(category)}: ${formatCurrency(item.amount)} (${getPercentage(item.amount)}%)`}
            ></div>
          ))}
        </div>
      </div>

      {/* Budget notes */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="text-sm">
            <strong className="text-yellow-800">💡 Nota:</strong>
            <span className="text-yellow-700 ml-1">
              Questi sono costi stimati. I prezzi effettivi potrebbero variare in base alle tecnologie selezionate e alle personalizzazioni richieste.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetBreakdown;